import api from "@/plugins/idb";

// очередь изменений для синхронизации с сервером (только пока авторизован)
const pendingAdd = new Set();
const pendingRemove = new Set();
let flushTimer = null;

// store — корневой vuex-стор (в экшенах это `this`), у него есть $axios
function resolveUserId(store) {
    return (
        (store.state.wishlist && store.state.wishlist.userId) ||
        (store.state.auth && store.state.auth.user && store.state.auth.user.id) ||
        null
    );
}

function resetPending() {
    pendingAdd.clear();
    pendingRemove.clear();
    if (flushTimer) {
        clearTimeout(flushTimer);
        flushTimer = null;
    }
}

function requeue(add_ids, remove_ids) {
    add_ids.forEach((id) => pendingAdd.add(id));
    remove_ids.forEach((id) => pendingRemove.add(id));
}

function scheduleFlush(store, delay = 400) {
    if (flushTimer) {
        return;
    }
    flushTimer = setTimeout(() => {
        flushTimer = null;
        flushNow(store);
    }, delay);
}

async function flushNow(store) {
    const add_ids = [...pendingAdd];
    const remove_ids = [...pendingRemove];
    pendingAdd.clear();
    pendingRemove.clear();
    if (!add_ids.length && !remove_ids.length) {
        return;
    }
    // вышли из аккаунта, пока очередь ждала — изменения не актуальны
    if (!(store.state.auth && store.state.auth.loggedIn)) {
        return;
    }
    const userId = resolveUserId(store);
    if (!userId) {
        requeue(add_ids, remove_ids);
        console.warn("[wishlist] sync skipped: no user_id yet");
        return;
    }
    try {
        await store.$axios.post("/api/user/wishlist", {
            user_id: userId,
            add_ids,
            remove_ids,
        });
    } catch (error) {
        requeue(add_ids, remove_ids);
        console.warn("[wishlist] sync failed:", (error.response && error.response.data) || error);
    }
}

export default {
    addItemToDb(context, item) {
        const id = parseInt(item);
        // авторизован: idb не используем, оптимистично в стор + очередь на сервер
        if (process.client && context.rootState.auth.loggedIn) {
            pendingRemove.delete(id);
            pendingAdd.add(id);
            context.commit("addWishlistId", id);
            scheduleFlush(this);
            return;
        }
        // гость: только локальное хранилище
        return api.saveWishlistItem(item);
    },
    async setIds(context) {
        // при авторизации источник истины — сервер, idb не перечитываем
        if (process.client && context.rootState.auth.loggedIn) {
            return;
        }
        const items = await api.getWishlist();
        context.commit("setWishlist", items);
    },
    deleteItemFromDb(context, item) {
        const id = parseInt(item);
        if (process.client && context.rootState.auth.loggedIn) {
            pendingAdd.delete(id);
            pendingRemove.add(id);
            context.commit("removeWishlistId", id);
            scheduleFlush(this);
            return;
        }
        return api.deleteWishlistItem(item);
    },
    // login: гостевые добавления доложим на аккаунт, idb очищаем.
    // Серверный список — источник истины, удаления с других браузеров не теряются.
    async syncOnLogin(context) {
        if (!process.client || context.getters.getLoading) {
            return;
        }
        context.commit("setLoading", true);
        resetPending();
        try {
            const { data } = await this.$axios.get("/api/user/wishlist");
            if (!data || !data.success) {
                return;
            }
            // пользователь успел выйти, пока синк выполнялся — не трогаем стор
            if (!(context.rootState.auth && context.rootState.auth.loggedIn)) {
                return;
            }
            const info = data.data || {};
            if (info.user_id) {
                context.commit("setUserId", info.user_id);
            }

            const serverIds = (info.ids || []).map((id) => parseInt(id)).filter((id) => !isNaN(id));
            const serverSet = new Set(serverIds);
            const guestIds = (await api.getWishlist()).map((id) => parseInt(id)).filter((id) => !isNaN(id));

            // докладываем гостевые добавления; серверные удаления НЕ откатываем
            const add_ids = guestIds.filter((id) => !serverSet.has(id));
            let guestMergePending = false;
            if (add_ids.length) {
                const userId = info.user_id || resolveUserId(this);
                let sent = false;
                if (userId) {
                    try {
                        await this.$axios.post("/api/user/wishlist", {
                            user_id: userId,
                            add_ids,
                            remove_ids: [],
                        });
                        sent = true;
                    } catch (error) {
                        console.warn("[wishlist] merge failed:", (error.response && error.response.data) || error);
                    }
                }
                if (!sent) {
                    // попробуем позже обычной очередью; idb не чистим, чтобы не потерять
                    guestMergePending = true;
                    requeue(add_ids, []);
                }
            }

            // гостевое хранилище съедено: idb — только для гостей
            if (!guestMergePending) {
                await api.clearWishlist();
            }

            // сервер + гостевые + оптимистичные изменения за время синка, минус отложенные удаления
            const current = (context.state.ids || []).map((id) => parseInt(id));
            const merged = [...new Set([...serverIds, ...guestIds, ...current])];
            pendingRemove.forEach((id) => {
                const idx = merged.indexOf(id);
                if (idx !== -1) {
                    merged.splice(idx, 1);
                }
            });
            if (!(context.rootState.auth && context.rootState.auth.loggedIn)) {
                return;
            }
            context.commit("setWishlist", merged);
        } catch (error) {
            console.warn("[wishlist] sync on login failed:", (error.response && error.response.data) || error);
        } finally {
            context.commit("setLoading", false);
            if (process.client) {
                scheduleFlush(this);
            }
        }
    },
    // logout: локальное избранное очищается
    async clearLocal(context) {
        resetPending();
        await api.clearWishlist();
        context.commit("setWishlist", []);
        context.commit("setUserId", null);
    },
};
